/**
 * PDP Image Zoom Modal
 * Vanilla JavaScript implementation for product image zoom functionality
 */

(function () {
  "use strict";

  let imageData = [];
  let currentImageIndex = 0;
  let lastFocusedElement = null;

  /**
   * Extract full-size image URL and convert to 2048px width
   * Based on Shopify's image_url filter documentation
   */
  function getFullSizeImageUrl(imgElement) {
    let url = null;

    // Check if image has data-src (lazy loaded)
    if (imgElement.dataset.src) {
      url = imgElement.dataset.src;
    }
    // Check if image has srcset
    else if (imgElement.srcset) {
      // Get the largest source from srcset
      const sources = imgElement.srcset.split(",").map((s) => s.trim());
      const largest = sources[sources.length - 1];
      if (largest) {
        url = largest.split(" ")[0];
      }
    }
    // Use current src
    else if (imgElement.src) {
      url = imgElement.src;
    }

    if (!url) return null;

    return convertTo2048pxUrl(url);
  }

  /**
   * Convert a Shopify image URL to 2048px width version
   * Handles both old format (_480x480.jpg) and new format (query parameters)
   */
  function convertTo2048pxUrl(url) {
    // Parse URL to separate base URL and query parameters
    const urlParts = url.split("?");
    let baseUrl = urlParts[0];
    let queryParams = urlParts[1] || "";

    // Remove size from filename (old format: image_480x480.jpg -> image.jpg)
    // Pattern: .../image_480x480.jpg or .../image_960x960.jpg
    baseUrl = baseUrl.replace(/_\d+x\d+\.(jpg|jpeg|png|webp|gif)/i, ".$1");

    // Remove size from path (old format: .../480x480/image.jpg -> .../image.jpg)
    baseUrl = baseUrl.replace(/\/\d+x\d+\//, "/");

    // Remove crop parameters from path (old format: .../480x480,crop/...)
    baseUrl = baseUrl.replace(/\/\d+x\d+,crop\//, "/");

    // Parse and update query parameters (new format)
    const params = new URLSearchParams(queryParams);

    // Remove existing width/height parameters
    params.delete("width");
    params.delete("height");

    // Add width=2048 parameter (Shopify's recommended format)
    params.set("width", "2048");

    // Preserve version parameter if it exists
    const version = params.get("v");
    if (version) {
      params.set("v", version);
    }

    // Reconstruct URL with query parameters
    const newQueryString = params.toString();
    return baseUrl + (newQueryString ? "?" + newQueryString : "");
  }

  /**
   * Extract image URL from source element and convert to 2048px width
   */
  function getImageUrlFromSource(sourceElement) {
    if (!sourceElement.srcset) return null;

    // Get the first URL from srcset (we'll convert it to 2048px)
    const url = sourceElement.srcset.split(" ")[0];

    return convertTo2048pxUrl(url);
  }

  /**
   * Collect all zoomable images from the gallery
   */
  function collectImages() {
    imageData = [];
    const gallery = document.querySelector(".pdp__gallery");
    if (!gallery) return;

    // Get all picture elements in the gallery
    const pictures = gallery.querySelectorAll("picture");

    pictures.forEach((picture, index) => {
      // Skip video thumbnails
      if (picture.closest(".pdp__gallery__video")) {
        return;
      }

      // Skip herb images (they're not product images)
      if (picture.closest(".herb-image")) {
        return;
      }

      // Get the img element
      const img = picture.querySelector("img");
      if (!img) return;

      // Get alt text
      const alt = img.alt || "Product image";

      // Try to get full-size URL from source elements first
      let fullSizeUrl = null;
      const sources = picture.querySelectorAll("source");

      if (sources.length > 0) {
        // Get the largest source (usually the last one)
        const largestSource = sources[sources.length - 1];
        fullSizeUrl = getImageUrlFromSource(largestSource);
      }

      // Fallback to img element
      if (!fullSizeUrl) {
        fullSizeUrl = getFullSizeImageUrl(img);
      }

      // If still no URL, try to construct from current src
      if (!fullSizeUrl && img.src) {
        fullSizeUrl = getFullSizeImageUrl(img);
      }

      if (fullSizeUrl) {
        imageData.push({
          url: fullSizeUrl,
          alt: alt,
          element: img,
          picture: picture,
        });
      }
    });

    // Also check for any standalone img elements not in picture tags
    const standaloneImgs = gallery.querySelectorAll("img:not(picture img)");
    standaloneImgs.forEach((img) => {
      // Skip if already collected or in video/herb
      if (img.closest(".pdp__gallery__video") || img.closest(".herb-image")) {
        return;
      }

      // Check if this img is already in our data
      const alreadyCollected = imageData.some((data) => data.element === img);
      if (alreadyCollected) return;

      const alt = img.alt || "Product image";
      const fullSizeUrl = getFullSizeImageUrl(img);

      if (fullSizeUrl) {
        imageData.push({
          url: fullSizeUrl,
          alt: alt,
          element: img,
          picture: null,
        });
      }
    });
  }

  /**
   * Open the image zoom modal
   */
  function openImageZoom(index) {
    if (index < 0 || index >= imageData.length) return;

    currentImageIndex = index;
    const modal = document.getElementById("pdp-image-zoom-modal");
    const modalImage = modal.querySelector(".image-zoom__image");
    const counter = modal.querySelector(".image-zoom__counter");
    const prevBtn = modal.querySelector(".image-zoom__prev");
    const nextBtn = modal.querySelector(".image-zoom__next");

    if (!modal || !modalImage) return;

    // Store the last focused element
    lastFocusedElement = document.activeElement;

    // Set image source
    const imageInfo = imageData[index];
    modalImage.src = imageInfo.url;
    modalImage.alt = imageInfo.alt;

    // Update counter
    if (counter) {
      counter.textContent = `${index + 1} / ${imageData.length}`;
    }

    // Update navigation buttons
    if (prevBtn) {
      prevBtn.disabled = index === 0;
    }
    if (nextBtn) {
      nextBtn.disabled = index === imageData.length - 1;
    }

    // Show modal
    modal.style.display = "block";
    document.body.style.overflow = "hidden";

    // Focus the modal for accessibility
    const modalWrap = modal.querySelector(".modal__wrap");
    if (modalWrap) {
      modalWrap.focus();
    }

    // Prevent body scroll
    document.body.classList.add("modal-open");
  }

  /**
   * Close the image zoom modal
   */
  function closeImageZoom() {
    const modal = document.getElementById("pdp-image-zoom-modal");
    if (!modal) return;

    modal.style.display = "none";
    document.body.style.overflow = "";
    document.body.classList.remove("modal-open");

    // Restore focus
    if (lastFocusedElement && typeof lastFocusedElement.focus === "function") {
      lastFocusedElement.focus();
    }
  }

  /**
   * Navigate to previous/next image
   */
  function navigateImage(direction) {
    if (direction === "prev" && currentImageIndex > 0) {
      openImageZoom(currentImageIndex - 1);
    } else if (
      direction === "next" &&
      currentImageIndex < imageData.length - 1
    ) {
      openImageZoom(currentImageIndex + 1);
    }
  }

  /**
   * Check if we're on desktop (width > 768px)
   */
  function isDesktop() {
    return window.innerWidth > 768;
  }

  /**
   * Initialize the image zoom functionality (desktop only)
   */
  function initPdpImageZoom() {
    // Only initialize on desktop
    if (!isDesktop()) {
      return;
    }

    // Collect images
    collectImages();

    if (imageData.length === 0) {
      return; // No images to zoom
    }

    const gallery = document.querySelector(".pdp__gallery");
    if (!gallery) return;

    // Add click handlers to all images
    imageData.forEach((imageInfo, index) => {
      const clickableElement = imageInfo.picture || imageInfo.element;
      if (clickableElement) {
        clickableElement.style.cursor = "pointer";
        clickableElement.addEventListener("click", function (e) {
          // Don't trigger if clicking on a video play button
          if (e.target.closest(".play-icon")) {
            return;
          }
          // Double-check we're still on desktop
          if (!isDesktop()) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          openImageZoom(index);
        });
      }
    });

    // Modal close handlers
    const modal = document.getElementById("pdp-image-zoom-modal");
    if (!modal) return;

    // Close button
    const closeBtn = modal.querySelector(".modal--close");
    if (closeBtn) {
      closeBtn.addEventListener("click", closeImageZoom);
    }

    // Background click
    const modalBg = modal.querySelector(".modal__bg");
    if (modalBg) {
      modalBg.addEventListener("click", closeImageZoom);
    }

    // Navigation buttons
    const prevBtn = modal.querySelector(".image-zoom__prev");
    const nextBtn = modal.querySelector(".image-zoom__next");

    if (prevBtn) {
      prevBtn.addEventListener("click", function (e) {
        e.stopPropagation();
        navigateImage("prev");
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", function (e) {
        e.stopPropagation();
        navigateImage("next");
      });
    }

    // Keyboard navigation
    document.addEventListener("keydown", function (e) {
      if (modal.style.display !== "block") return;

      switch (e.key) {
        case "Escape":
          closeImageZoom();
          break;
        case "ArrowLeft":
          e.preventDefault();
          navigateImage("prev");
          break;
        case "ArrowRight":
          e.preventDefault();
          navigateImage("next");
          break;
      }
    });

    // Prevent modal from closing when clicking on the image
    const modalImage = modal.querySelector(".image-zoom__image");
    if (modalImage) {
      modalImage.addEventListener("click", function (e) {
        e.stopPropagation();
      });
    }

    const modalWrap = modal.querySelector(".modal__wrap");
    if (modalWrap) {
      modalWrap.addEventListener("click", function (e) {
        e.stopPropagation();
      });
    }
  }

  // Initialize when DOM is ready (desktop only)
  function setupImageZoom() {
    if (isDesktop()) {
      initPdpImageZoom();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setupImageZoom);
  } else {
    setupImageZoom();
  }

  // Handle window resize to enable/disable functionality
  let resizeTimeout;
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(function () {
      // If we're on desktop and haven't initialized, do it now
      if (isDesktop() && imageData.length === 0) {
        setupImageZoom();
      }
      // If we're on mobile and modal is open, close it
      else if (!isDesktop()) {
        const modal = document.getElementById("pdp-image-zoom-modal");
        if (modal && modal.style.display === "block") {
          closeImageZoom();
        }
      }
    }, 250);
  });

  // Re-initialize if gallery content changes (for dynamic content, desktop only)
  const gallery = document.querySelector(".pdp__gallery");
  if (gallery) {
    const observer = new MutationObserver(function (mutations) {
      // Re-collect images if gallery content changes (only on desktop)
      if (isDesktop()) {
        collectImages();
      }
    });

    observer.observe(gallery, {
      childList: true,
      subtree: true,
    });
  }
})();
